<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.settings')"
    agree-text="dialog.save"
    dialog-width="600px"
    min-body-height="50px"
    no-paddings-y
    no-paddings-x
    header-shadow
    @agree="save"
    :loading="saving"
  >
    <template #body>
      <v-row fill-height no-gutters>
        <v-col v-if="isFetching">
          <main-loader></main-loader>
        </v-col>
        <v-col v-else class="px-10 py-4">
          <admin-wrapper
            hide-title-row
            :has-buttons="false"
            :inputs="inputs"
            :is-super-admin="true"
            :is-form-fetched="!isFetching"
          >
            <template #[`avatar.input`]>
              <div class="tw:flexcenter tw:flexcol-2 tw:grow">
                <file-input ref="file" :options="avatarUploadOptions">
                  <template #uploadContent>
                    <div class="tw:flexcenter tw:flex-col tw:gap-2">
                      <div class="tw:relative tw:hover:[&>div]:flexcenter tw:hover:[&>div]:cursor-pointer">
                        <div
                          style="display: none"
                          class="tw:bg-black/50 tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:z-[1] tw:rounded-full"
                        >
                          <v-icon color="white">mdi-camera</v-icon>
                        </div>
                        <user-avatar
                          @onAvatar.once="onAvatarState"
                          :settings="{ size: 60 }"
                          :custom-avatar="
                            ![undefined, true].includes(loadedAvatarPreview) ? loadedAvatarPreview : undefined
                          "
                        />
                      </div>
                      <base-button v-if="loadedAvatarPreview" type="text" color="error" @click="removeAvatar">
                        {{ $i18n.t('dialog.userSettings.removeAvatar') }}
                      </base-button>
                    </div>
                  </template>
                </file-input>
                <main-dialog
                  :is-visible.sync="isAvatarDialogVisible"
                  @modalClosed="onModalClosed"
                  @agree="saveAvatarAction"
                  agree-text="dialog.agreeSave"
                  modal-width="320px"
                  :transition="false"
                >
                  <template #body>
                    <cropper-wrapper ref="cropper" :image-src="uploadedAvatarSrc" :key="uploadedAvatarSrc" />
                  </template>
                </main-dialog>
              </div>
            </template>
            <template #[`attribute_table_row_height.input`]="data">
              <v-col cols="7">
                <data-input
                  v-bind="data"
                  v-model="params[data.name]"
                  :data-type="{ name: 'slider' }"
                  :min="tableRowHeightRange.min"
                  :max="tableRowHeightRange.max"
                >
                  <template #valueLabel>
                    <v-col cols="3" class="value-label pl-0 text-center pt-3 d-flex align-center" align-self="center">
                      <data-input
                        style="width: 57px !important; min-width: 57px !important"
                        v-model="params[data.name]"
                        :data-type="{ name: 'integer' }"
                        @input="inputTableRowHeightInput"
                      />
                      <span class="pl-2">px</span>
                    </v-col>
                  </template>
                </data-input>
              </v-col>
            </template>
            <template #[`key_bindings.input`]="data">
              <key-bindings v-model="params[data.name]" />
            </template>
          </admin-wrapper>
        </v-col>
      </v-row>
    </template>
  </main-large-dialog>
</template>

<script>
import MainLargeDialog from '@/components/MainLargeDialog';
import UserAvatar from '@/components/UserAvatar';
import MainDialog from '@/components/MainDialog';
import FileInput from '@/components/FileInput';
import CropperWrapper from '@/components/CropperWrapper';
import MainLoader from '@/components/MainLoader';
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import KeyBindings from '@/components/KeyBindings';

import { get, call } from 'vuex-pathify';

let uploadedAvatarFile;
let croppedAvatarBlob;

export default {
  name: 'UserSettingsDialog',
  components: {
    MainLargeDialog,
    MainDialog,
    MainLoader,
    CropperWrapper,
    AdminWrapper,
    DataInput,
    KeyBindings,
    FileInput,
    UserAvatar,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isAvatarDialogVisible: false,
    uploadedAvatarSrc: null,
    tableRowHeightRange: {
      min: 25,
      max: 48,
    },
    loadedAvatarPreview: undefined,
    params: {
      attribute_table_row_height: 0,
      key_bindings: [],
    },
    isFetching: false,
    saving: false,
    notNamedSettings: ['key_bindings'],
  }),
  computed: {
    modules: get('admin/additionalModules'),
    userSettings: get('users/currentUser@settings'),
    userSettingsModules() {
      return {
        key_bindings: {
          moduleName: 'KEY_BINDINGS',
        },
      };
    },
    userId: get('users/currentUser@id'),
    keyBindings: get('admin/settingsValues@key_bindings_tools_enabled'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    avatarUploadOptions() {
      return {
        url: '/',
        acceptedFiles: '.jpeg,.jpg,.png',
        autoProcessQueue: false,
        uploadMultiple: false,
        maxFiles: 1,
        accept: file => {
          uploadedAvatarFile = file;
          this.isAvatarDialogVisible = true;
        },
        init: function () {
          this.on('addedfile', function () {
            this.removeAllFiles(true);
          });
        },
      };
    },
    inputs() {
      return [
        {
          name: 'avatar',
          translationPath: 'dialog.userSettings.avatar',
          titleCols: 5,
          align: 'center',
        },
        ...Object.keys(this.userSettings)
          .filter(name => {
            if (!this.userSettingsModules[name]) return true;
            else {
              const { moduleName /*, settingName */ } = this.userSettingsModules[name];
              return !moduleName || this.modules[moduleName]?.enabled;
            }
          })
          .map(name => {
            return {
              name,
              translationPath: `dialog.userSettings.${name}`,
              titleCols: 5,
              align: 'center',
              rules: [v => !!v || v == '0' || 'rules.required'],
              ...(this.notNamedSettings.includes(name) ? { isNamedSlot: false } : {}),
            };
          }),
      ];
    },
  },
  methods: {
    saveAvatar: call('users/saveAvatar'),
    deleteAvatar: call('users/deleteAvatar'),
    editUserSettings: call('users/editUserSettings'),
    inputTableRowHeightInput(e) {
      if (!e) this.params['attribute_table_row_height'] = this.tableRowHeightRange.min;
    },
    async save() {
      this.saving = true;
      try {
        let savePromises = [
          this.editUserSettings({
            userId: this.userId,
            payload: this.params,
          }),
        ];
        if (croppedAvatarBlob) {
          const formData = new FormData();
          const file = new File([croppedAvatarBlob], `avatar.${croppedAvatarBlob.type.split('/').at(-1)}`, {
            type: croppedAvatarBlob.type,
          });
          formData.append('file', file);
          savePromises.push(this.saveAvatar(formData));
        } else if (!this.loadedAvatarPreview) {
          savePromises.push(this.deleteAvatar());
        }
        await Promise.all(savePromises);
        if (savePromises.length > 1) this.$root.$emit('onAvatarChanged');
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successSaveChanges'),
        });
      } finally {
        this.saving = false;
        this.computedIsVisible = false;
      }
    },
    onAvatarState(avatar) {
      this.loadedAvatarPreview = avatar;
    },
    removeAvatar() {
      this.loadedAvatarPreview = croppedAvatarBlob = null;
    },
    async saveAvatarAction() {
      const canvas = await this.$refs.cropper.generateImage({ height: 300 });
      canvas.toBlob(blob => {
        croppedAvatarBlob = blob;
        this.loadedAvatarPreview = URL.createObjectURL(croppedAvatarBlob);
        this.onModalClosed();
        this.isAvatarDialogVisible = false;
      });
    },
    onModalClosed() {
      uploadedAvatarFile = undefined;
      this.uploadedAvatarSrc = null;
    },
  },
  watch: {
    isAvatarDialogVisible: {
      async handler(nV) {
        if (!nV || !uploadedAvatarFile) return;
        await this.$nextTick();
        this.uploadedAvatarSrc = URL.createObjectURL(uploadedAvatarFile);
      },
    },
    userSettings: {
      immediate: true,
      handler(nV) {
        if (!nV) return;
        Object.entries(nV).map(([name, value]) => {
          if (name === 'key_bindings') {
            this.params[name] = this.keyBindings.map(keyBinding => {
              const keyBind = value.find(bind => bind.verboseName === keyBinding.verboseName)?.keyBind;
              return { ...keyBinding, keyBind };
            });
          } else {
            this.params[name] = value;
          }
        });
      },
    },
  },
};
</script>
